import { z } from "zod";

import {
  instagramPublishPackageSchema,
  type InstagramPublishCarouselSlide,
  type InstagramPublishPackage,
  type SocialPostGenerationTaskResultV1,
  type SocialPostPublicationStatus,
} from "./generated-content.ts";
import type { ScheduledPostApprovalStatus } from "./campaign.ts";
import type { SocialPostEditableDraft } from "./post-edit.ts";
import { INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES, socialPostTypeSchema } from "./social-post-scene.ts";
import {
  SOCIAL_POST_CAROUSEL_MAX_SLIDES,
  SOCIAL_POST_CAROUSEL_MIN_SLIDES,
} from "./social-post-carousel.ts";

export const publishableInstagramPostTypeSchema = z.union([
  z.literal("feed_square"),
  z.literal("feed_portrait"),
  z.literal("carousel_portrait"),
  z.literal("carousel_square"),
]);
export type PublishableInstagramPostType = z.infer<typeof publishableInstagramPostTypeSchema>;

export const socialPostPublishReadinessSchema = z.object({
  isPublishable: z.boolean(),
  issues: z.array(z.string().min(1)).default([]),
});
export type SocialPostPublishReadiness = z.infer<typeof socialPostPublishReadinessSchema>;

export const isInstagramCarouselPublishPostType = (
  postType: string,
): postType is (typeof INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES)[number] =>
  (INSTAGRAM_CAROUSEL_SOCIAL_POST_TYPES as readonly string[]).includes(postType);

const compareCarouselPublishSlides = (
  left: InstagramPublishCarouselSlide,
  right: InstagramPublishCarouselSlide,
) => left.slideIndex - right.slideIndex;

const uniqueCarouselPublishSlides = (
  slides: InstagramPublishCarouselSlide[],
): InstagramPublishCarouselSlide[] => {
  const byIndex = new Map<number, InstagramPublishCarouselSlide>();
  for (const slide of slides.toSorted(compareCarouselPublishSlides)) {
    if (!byIndex.has(slide.slideIndex)) {
      byIndex.set(slide.slideIndex, slide);
    }
  }
  return Array.from(byIndex.values());
};

export const deriveInstagramCarouselPublishSlides = (input: {
  postType: z.infer<typeof socialPostTypeSchema>;
  publishedDraft?: SocialPostEditableDraft | null;
  generationResult?: SocialPostGenerationTaskResultV1 | null;
}): InstagramPublishCarouselSlide[] => {
  if (!isInstagramCarouselPublishPostType(input.postType)) {
    return [];
  }

  if (input.publishedDraft?.carouselSlides && input.publishedDraft.carouselSlides.length > 0) {
    return uniqueCarouselPublishSlides(
      input.publishedDraft.carouselSlides.map((slide) => ({
        slideId: slide.slideId,
        slideIndex: slide.slideIndex,
      })),
    );
  }

  if (
    input.generationResult?.carouselSlidePlans &&
    input.generationResult.carouselSlidePlans.length > 0
  ) {
    return uniqueCarouselPublishSlides(
      input.generationResult.carouselSlidePlans.map((slide) => ({
        slideId: slide.slideId,
        slideIndex: slide.slideIndex,
      })),
    );
  }

  return uniqueCarouselPublishSlides(
    (input.generationResult?.carouselPlan?.slides ?? []).map((slide) => ({
      slideId: slide.id,
      slideIndex: slide.index,
    })),
  );
};

export const deriveInstagramPublishPackage = (input: {
  postType: z.infer<typeof socialPostTypeSchema>;
  assetIds: string[];
  caption?: string | null;
  hashtags?: string[];
  altText?: string | null;
  carouselSlides?: InstagramPublishCarouselSlide[];
}): InstagramPublishPackage =>
  instagramPublishPackageSchema.parse({
    platform: "instagram",
    postType: input.postType,
    assetIds: input.assetIds,
    caption: input.caption ?? null,
    hashtags: input.hashtags ?? [],
    altText: input.altText ?? null,
    ...(input.carouselSlides && input.carouselSlides.length > 0
      ? { carouselSlides: uniqueCarouselPublishSlides(input.carouselSlides) }
      : {}),
  });

export const assessInstagramPublishReadiness = (input: {
  postStatus: string;
  approvalStatus: ScheduledPostApprovalStatus;
  publicationStatus?: SocialPostPublicationStatus | null;
  postType: z.infer<typeof socialPostTypeSchema>;
  assetIds: string[];
  carouselSlideCount?: number | null;
  hasInstagramConnection: boolean;
}) => {
  const issues: string[] = [];

  if (input.postStatus !== "generated") {
    issues.push("Post must be generated before it can be published.");
  }

  if (input.approvalStatus !== "approved") {
    issues.push("Post must be approved before it can be published.");
  }

  if (!publishableInstagramPostTypeSchema.safeParse(input.postType).success) {
    issues.push(
      "Only Instagram feed image posts and image carousels are currently supported for publishing.",
    );
  }

  if (isInstagramCarouselPublishPostType(input.postType)) {
    const carouselSlideCount = input.carouselSlideCount ?? 0;
    if (
      carouselSlideCount < SOCIAL_POST_CAROUSEL_MIN_SLIDES ||
      carouselSlideCount > SOCIAL_POST_CAROUSEL_MAX_SLIDES
    ) {
      issues.push(
        `Carousel posts require ${SOCIAL_POST_CAROUSEL_MIN_SLIDES}-${SOCIAL_POST_CAROUSEL_MAX_SLIDES} rendered slides before publishing.`,
      );
    }
  } else if (input.assetIds.length === 0) {
    issues.push("At least one generated asset is required for publishing.");
  }

  if (!input.hasInstagramConnection) {
    issues.push("An Instagram account must be connected before publishing.");
  }

  if (input.publicationStatus === "publishing") {
    issues.push("Post is already being published.");
  }

  if (input.publicationStatus === "published") {
    issues.push("Post has already been published.");
  }

  return socialPostPublishReadinessSchema.parse({
    isPublishable: issues.length === 0,
    issues,
  });
};
